import { Button, Popconfirm } from "antd";
import { useState } from "react";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import type { ActivityDemandConfig } from "@/views/pages/productActivity/types";

import AddRequirementMatchDialog from "./addRequirementMatchDialog";

export interface RequirementMatchListProps {
    value?: ActivityDemandConfig[];
    onChange?: (value: ActivityDemandConfig[]) => void;
    disabled?: boolean;
}

const inputTypeLabels: Record<number, string> = {
    1: "文本框",
    2: "单选框",
    3: "多选框",
    4: "地址",
    5: "日期时间",
    6: "多行文本框"
};

/** 复刻 Vue 需求对接配置列表，并把新增/编辑弹窗作为真实受控交互接入表单。 */
export default function RequirementMatchList({ value = [], onChange, disabled = false }: RequirementMatchListProps) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | undefined>();
    /** 需求字段表格复用旧 Vue 的 zhihao 表格列与操作入口。 */
    const columns: TableColumnProps<ActivityDemandConfig>[] = [
        { type: "index", label: "序号", width: 68 },
        { label: "字段名称", prop: "fieldName", minWidth: 180, render: ({ row }) => row.fieldName || "-" },
        {
            label: "是否必填",
            prop: "hasRequired",
            width: 100,
            render: ({ row }) => (Number(row.hasRequired) === 1 ? "是" : "否")
        },
        {
            label: "输入类型",
            prop: "inputType",
            width: 120,
            render: ({ row }) => inputTypeLabels[Number(row.inputType) || 1]
        },
        {
            label: "操作",
            prop: "operation",
            width: 140,
            fixed: "right",
            render: ({ row }) => {
                const index = value.indexOf(row);
                return (
                    <div className="activity-form__demand-actions">
                        <Button
                            disabled={disabled}
                            type="link"
                            onClick={() => {
                                setEditingIndex(index);
                                setDialogOpen(true);
                            }}
                        >
                            编辑
                        </Button>
                        <Popconfirm
                            disabled={disabled}
                            title="确定删除此需求字段吗？"
                            onConfirm={() => onChange?.(value.filter((_item, itemIndex) => itemIndex !== index))}
                        >
                            <Button danger disabled={disabled} type="link">
                                删除
                            </Button>
                        </Popconfirm>
                    </div>
                );
            }
        }
    ];

    return (
        <div className="activity-form__demand-editor" aria-label="需求对接配置列表">
            <div className="activity-form__demand-toolbar-row">
                <Button
                    disabled={disabled}
                    aria-label="新增需求字段"
                    type="primary"
                    onClick={() => {
                        setEditingIndex(undefined);
                        setDialogOpen(true);
                    }}
                >
                    新增
                </Button>
            </div>
            <div className="activity-form__demand-table">
                <ZhTable
                    columns={columns}
                    data={value}
                    pagination={false}
                    rowKey={row => `${row.fieldName || "field"}-${value.indexOf(row)}`}
                />
            </div>
            <ZhDialog
                open={dialogOpen}
                title={editingIndex == null ? "新增字段" : "编辑字段"}
                width="640px"
                onCancel={() => setDialogOpen(false)}
                footer={null}
            >
                <AddRequirementMatchDialog
                    initialValue={editingIndex == null ? undefined : value[editingIndex]}
                    onCancel={() => setDialogOpen(false)}
                    onSubmit={item => {
                        const next = [...value];
                        if (editingIndex == null) next.push(item);
                        else next[editingIndex] = item;
                        onChange?.(next);
                        setDialogOpen(false);
                    }}
                />
            </ZhDialog>
        </div>
    );
}
